import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import dayjs from 'dayjs';
import { Observable, iif, of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { BlockItem } from 'src/app/admin/models/admin.model';
import { CartItem } from 'src/app/cart/cart.model';
import { BackendService } from 'src/app/core/backend.service';
import { getRecurrenceFreq } from 'src/app/core/utilities';
import * as TreeActions from '../object-tree-store/actions';
import { ItemTreeHierarchy } from '../object-tree-store/object-tree.models';
import { selectTree } from '../object-tree-store/selects';
import { RootState } from '../root-state.model';
import { NavigateTo } from '../router/actions';
import { selectOrderId, selectOrderIdQueryString, selectOrderIdStatusPage } from '../router/selectors';
import { SetSchedulerErrors } from '../scheduler-store/actions';
import { SetTimePickerErrors, SetTimePickerValues } from '../time-picker-store/actions';
import * as CartActions from './actions';

@Injectable()
export class CartEffects {
  constructor(
    private actions$: Actions,
    private backend: BackendService,
    private store: Store<RootState>
  ) { }

  loadCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.LoadCart),
      withLatestFrom(this.store.select(selectOrderIdQueryString), this.store.select(selectOrderIdStatusPage)),
      switchMap(([action, orderIdQueryString, orderIdStatusPage]) =>
        this.backend.getCart(orderIdQueryString ? 'Admin' : (orderIdStatusPage ? 'Status' : 'Default'), action.orderId).pipe(
          map(cart => CartActions.LoadCartSuccess({
            cart: {
              ...cart,
              allowAddEditItems: cart.items.length === 0 || cart.items.some(i => i.allowEdit || i.allowDelete || i.permissionChangeReservation)
            }
          })),
          catchError(error => of(CartActions.LoadCartFailure({ error })))
        )
      )
    )
  )

  loadOrderCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.LoadOrder),
      withLatestFrom(this.store.select(selectOrderIdQueryString), this.store.select(selectOrderIdStatusPage)),
      switchMap(([, orderIdQueryString, orderIdStatusPage]) =>
        this.backend.getOrder(orderIdQueryString ? 'Admin' : (orderIdStatusPage ? 'Status' : 'Default')).pipe(
          map(order => CartActions.LoadOrderSuccess({ order }))
        )
      )
    )
  )

  loadCheckoutFields$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.LoadCheckoutFields),
      switchMap(() =>
        this.backend.getCheckoutFields().pipe(
          map(checkoutFields => CartActions.LoadCheckoutFieldsSuccess({ checkoutFields }))
        )
      )
    )
  )

  editReservation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.EditReservation),
      tap(editAction => {
        if (editAction.cartItem == null) {
          // unset all errors if edit-mode was left
          this.store.dispatch(TreeActions.SetTreeErrors({ errors: null }))
          this.store.dispatch(SetTimePickerErrors({ errors: null }))
          this.store.dispatch(SetSchedulerErrors({ errors: null }))
        }
      }),
      withLatestFrom(this.store.select(selectTree)),
      // check whether Tree is loaded. If not: load it and return it.
      switchMap(([editAction, tree]) => {
        return iif(() => tree !== null,
          // use current value
          of([editAction, tree]),
          // use Tree from backend
          this.backend.getItemTree().pipe(
            tap(items => this.store.dispatch(TreeActions.SetTreeItems({ items }))),
            map(treeAndItems => [editAction, treeAndItems.tree])
          )) as Observable<[{ cartItem: CartItem | BlockItem | null }, ItemTreeHierarchy[]]>
      }),
      switchMap(([editAction, tree]) => {
        if (!editAction.cartItem)
          return []

        let start = 'start' in editAction.cartItem ? dayjs(editAction.cartItem.start) : dayjs(editAction.cartItem.startTimeRaw)
        let end = 'end' in editAction.cartItem ? dayjs(editAction.cartItem.end) : dayjs(editAction.cartItem.endTimeRaw)
        const availabilityPlanId = 'availabilityPlanId' in editAction.cartItem ? editAction.cartItem.availabilityPlanId : undefined
        const preparationMinutes = 'preparationMinutes' in editAction.cartItem ? editAction.cartItem.preparationMinutes : undefined
        const followUpMinutes = 'followUpMinutes' in editAction.cartItem ? editAction.cartItem.followUpMinutes : undefined

        return [
          // update all common fields if in edit-mode or edit-mode exited
          SetTimePickerValues({
            start,
            end,
            availabilityPlanId,
            recurrenceFreq: 'recurrence' in editAction.cartItem ? getRecurrenceFreq(editAction.cartItem.recurrence) : null,
            quantity: 'quantity' in editAction.cartItem ? editAction.cartItem.quantity : null,
            preparationMinutes,
            followUpMinutes
          }),
          TreeActions.SetSelectedItemIds({ items: [editAction.cartItem.itemId!] })
        ]
      })
    )
  )

  copyReservation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.CopyReservation),
      withLatestFrom(this.store.select(selectOrderId)),
      switchMap(([editAction, orderId]) => [
        SetTimePickerValues({
          start: dayjs(editAction.cartItem.startTimeRaw),
          end: dayjs(editAction.cartItem.endTimeRaw),
          availabilityPlanId: editAction.cartItem.availabilityPlanId,
          recurrenceFreq: getRecurrenceFreq(editAction.cartItem.recurrence) ?? null,
          quantity: editAction.cartItem.quantity
        }),
        TreeActions.SetSelectedItemIds({ items: [editAction.cartItem.itemId!] }),
        NavigateTo({ route: 'Home', queryParams: { OrderId: orderId } })
      ])
    )
  )

  selectItemInObjectChoice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.SelectItemInObjectChoice),
      withLatestFrom(this.store.select(selectOrderId)),
      switchMap(([editAction, orderId]) => [
        TreeActions.SetSelectedItemIds({ items: [editAction.itemId] }),
        NavigateTo({ route: 'Home', queryParams: { OrderId: orderId } })
      ])
    )
  )
}
